import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { PageTransition, FadeIn } from '../components/layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ChevronRight, Mail, Lock, Loader2, AlertCircle } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get the page user was trying to access
  const from = location.state?.from?.pathname || '/mon-compte';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    const result = await login(email, password);
    
    if (result.success) {
      navigate(from, { replace: true });
    } else {
      setError(result.error);
    }
    
    setIsSubmitting(false);
  };

  return (
    <PageTransition>
      {/* Hero */}
      <section className="pt-32 pb-8 px-6 bg-gradient-to-b from-[var(--champagne)]/30 to-white">
        <div className="max-w-[800px] mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light mb-4 text-[var(--charcoal)]"
          >
            Connexion
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-[var(--charcoal)]/60"
          >
            Accédez à votre espace personnel
          </motion.p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white py-4 border-b border-[var(--champagne)]">
        <div className="max-w-[1400px] mx-auto px-6">
          <nav className="flex items-center gap-2 text-sm text-[var(--charcoal)]/60">
            <Link to="/" className="hover:text-[var(--gold)] transition-colors">Accueil</Link>
            <ChevronRight size={14} />
            <span className="text-[var(--charcoal)]">Connexion</span>
          </nav>
        </div>
      </div>

      {/* Login Form */}
      <section className="py-16 px-6" data-testid="login-section">
        <div className="max-w-[450px] mx-auto">
          <FadeIn>
            <form
              onSubmit={handleSubmit}
              className="bg-white p-8 md:p-10 border border-[var(--champagne)] shadow-sm"
              data-testid="login-form"
            >
              {/* Error Message */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-3"
                  data-testid="login-error"
                >
                  <AlertCircle size={18} className="shrink-0 mt-0.5" />
                  <span>{error}</span>
                </motion.div>
              )}

              {/* Email Field */}
              <div className="mb-6">
                <Label
                  htmlFor="email"
                  className="text-sm uppercase tracking-wider text-[var(--charcoal)]/70 mb-2 block"
                >
                  Email
                </Label>
                <div className="relative">
                  <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--charcoal)]/40" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="votre@email.com"
                    className="pl-10 rounded-none border-[var(--champagne)] focus:border-[var(--gold)] focus:ring-[var(--gold)]"
                    required
                    data-testid="login-email"
                  />
                </div>
              </div>

              {/* Password Field */}
              <div className="mb-8">
                <Label
                  htmlFor="password"
                  className="text-sm uppercase tracking-wider text-[var(--charcoal)]/70 mb-2 block"
                >
                  Mot de passe
                </Label>
                <div className="relative">
                  <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--charcoal)]/40" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-10 rounded-none border-[var(--champagne)] focus:border-[var(--gold)] focus:ring-[var(--gold)]"
                    required
                    data-testid="login-password"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-6 bg-[var(--gold)] text-white text-xs uppercase tracking-[0.2em] font-medium hover:bg-[var(--dark-gold)] transition-colors duration-300 rounded-none disabled:opacity-50"
                data-testid="login-submit"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin mr-2" />
                    Connexion en cours...
                  </>
                ) : (
                  'Se connecter'
                )}
              </Button>

              {/* Register Link */}
              <p className="mt-6 text-center text-sm text-[var(--charcoal)]/60">
                Pas encore de compte ?{' '}
                <Link
                  to="/register"
                  className="text-[var(--dark-rosegold)] hover:text-[var(--gold)] transition-colors font-medium"
                  data-testid="link-to-register"
                >
                  Créer un compte
                </Link>
              </p>
            </form>
          </FadeIn>
        </div>
      </section>
    </PageTransition>
  );
}
